import { memo, useCallback, useMemo } from 'react';
import { withErrorBoundary } from 'react-error-boundary';
import styled from 'styled-components';
import { useContentfulInspectorMode } from '@contentful/live-preview/react';
import { Spacing8, Spacing16 } from 'autogen/design-tokens/tokens';
import ContentCard from 'components/ContentCard';
import type { ContentCardProps } from 'components/ContentCard/types';
import DynamicItemCarousel from 'components/generic/ScrollSnapSlider/components/DynamicItemCarousel';
import { Typography } from 'components/generic/typography';
import { removeListStyling } from 'modules/helpers/mixins';
import type { TypeArticleCardDetailed } from 'types/autogen/contentful';
import { filterPublishedFields, navigationLinkToPageAction } from '../../shared/ContentfulHelpers';
import { createSrcSet } from '../Image/shared/helpers';

type TypeFields = NonNullable<TypeArticleCardDetailed['fields']>;
type TypeFieldLinks = NonNullable<TypeFields['links']>;

const GridLayout = styled.ul`
    ${removeListStyling}
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
    gap: ${Spacing16};
`;

const HeaderText = styled(Typography.Headline)`
    margin-block: ${Spacing8};
`;

const LinkItem = memo(({ link, ...restProps }: { link: TypeFieldLinks[number] }) => {
    const inspectorProps = useContentfulInspectorMode({ entryId: link.sys.id });

    const cardProps: ContentCardProps | null = useMemo(() => {
        const {
            fields: { headline, imageRatio43, section, linkText }
        } = link;

        const linkAction = navigationLinkToPageAction(link);
        if (!linkAction) return null;

        const imgSrc = imageRatio43?.fields.image?.fields.file?.url;
        const imgAlt = imageRatio43?.fields.altText;
        const imgFile = imageRatio43?.fields.image?.fields.file;

        if (!imgSrc || !imgAlt) return null;

        return {
            link: {
                label: linkText,
                action: linkAction
            },
            subheading: section?.fields.publicName,
            heading: headline,
            type: 'default',
            showBorder: false,
            image: {
                src: imgSrc,
                alt: imgAlt,
                srcSet: imgFile ? createSrcSet(imgFile) : ''
            }
        };
    }, [link]);

    if (!cardProps) return null;

    return <ContentCard {...cardProps} {...restProps} {...inspectorProps({ fieldId: 'internalName' })} />;
});

const Grid = memo(({ links }: { links: TypeFieldLinks }) => {
    const linkItems = useMemo(
        () =>
            links.map((link, index) => (
                <li key={link?.sys.id || index}>
                    <LinkItem link={link} />
                </li>
            )),
        [links]
    );

    return <GridLayout>{linkItems}</GridLayout>;
});

const Slider = memo(({ links }: { links: TypeFieldLinks }) => {
    const itemRenderer = useCallback(
        (link, index) => <LinkItem link={link} key={index} data-slide-index={index} />,
        []
    );

    return <DynamicItemCarousel gapSize={Spacing16} items={links} itemRenderer={itemRenderer} />;
});

function Layout({ layout, links }: { layout: TypeFields['layout']; links: TypeFieldLinks }) {
    switch (layout) {
        case 'Slider':
            return <Slider links={links} />;
        case 'Grid':
        default:
            return <Grid links={links} />;
    }
}

function ArticleCard(props: TypeArticleCardDetailed) {
    const {
        fields: { layout, links, title },
        sys: { id }
    } = props;
    const linksToRender = useMemo(() => (links ? filterPublishedFields(links) : []), [links]);
    const inspectorProps = useContentfulInspectorMode({ entryId: id });

    if (!links || linksToRender.length === 0) return null;

    return (
        <div {...inspectorProps({ fieldId: 'internalName' })}>
            <HeaderText size="M">{title}</HeaderText>
            <Layout layout={layout} links={linksToRender} />
        </div>
    );
}

export default withErrorBoundary(ArticleCard, {
    fallback: null,
    onError(error, info) {
        console.error(error, info);
    }
});
